import React from 'react';
import axios from "axios";
import validator from 'validator';
import { Container, useMantineTheme, Box, SimpleGrid, Card, Button, LoadingOverlay } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useLoginContext } from "../../context/loginContext";

import { BE_API_AUTH, utils } from "../../utils/globalVariables";
import { regexNoSpace, safeValue } from "../../utils/globalFunctions";

import ModalResponse from "../../components/general/ModalResponse";
import InputComponent from "../../components/general/InputComponent";

const AccountResetEmail = () => {

    const mountedRef = React.useRef(true);

    const theme = useMantineTheme();
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorGetInitData: 'accountResetEmail-1',
        errorSubmitEmail: 'accountResetEmail-2',
        successSubmitEmail: 'accountResetEmail-3',
    })

    const [data, setData] = React.useState({
        dataInitLoading: false,

        email: {
            value: '',
            valueError: '',
            submitLoading: false,
        },
        modalResponse: {
            show: false,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: false,
            content: ''
        }
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    React.useEffect(() => {
        setData(d => ({ ...d, dataInitLoading: true }))

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);

        axios({
            method: 'post',
            url: BE_API_AUTH + '/get_user_email',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataInitLoading: false }));

            if (response.data.flag_status === 'isset') {
                setData(d => ({
                    ...d,
                    email: { ...d.email, value: response.data.email },
                }));
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({
                ...d,
                dataInitLoading: false,
            }));

            notifications.show({
                id: notificationId.current.errorGetInitData,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }, [handleSetLoginContext, loginContext.syswebapp])

    const submitEmail = () => {
        setData(d => ({ ...d, email: { ...d.email, valueError: '' } }));

        if (data.email.value && !validator.isEmail(data.email.value)) {
            setData(d => ({ ...d, email: { ...d.email, valueError: 'Format Email Tidak Valid' } }));
        } else {
            setData(d => ({ ...d, email: { ...d.email, submitLoading: true } }));

            const formDT = new FormData();
            const formDTHeader = { 'Content-Type': 'application/json' }

            formDT.append('json_token', loginContext.syswebapp);
            formDT.append('email', data.email.value);

            axios({
                method: 'post',
                url: BE_API_AUTH + '/update_user_email',
                data: formDT,
                headers: formDTHeader,
            }).then((response) => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, email: { ...d.email, submitLoading: false } }));

                if (response.data.flag_status === 'isset') {
                    notifications.show({
                        id: notificationId.current.successSubmitEmail,
                        title: utils.notification.success.caption,
                        message: 'Berhasil Submit Data.',
                        color: utils.notification.success.color,
                        icon: utils.notification.success.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'duplicate-email') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: false,
                            content: `Email ${response.data.suspect} sudah digunakan oleh user lain.`,
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'error') {
                    notifications.show({
                        id: notificationId.current.errorSubmitEmail,
                        title: utils.notification.warning.caption,
                        message: response.data.pesan,
                        color: utils.notification.warning.color,
                        icon: utils.notification.warning.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'invalid-json-token') {
                    handleSetLoginContext(null);
                }

                if (response.data.flag_status === 'user-inactive') {
                    handleSetLoginContext(null);
                }
            }).catch(error => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, email: { ...d.email, submitLoading: false } }));

                notifications.show({
                    id: notificationId.current.errorSubmitEmail,
                    title: utils.notification.warning.caption,
                    message: error.message,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: true,
                })
            })
        }
    }

    return (
        <React.Fragment>
            <Container fluid>
                <SimpleGrid
                    cols={1}
                    spacing='sm'
                    mt='2rem'
                    breakpoints={[
                        { minWidth: 'xl', cols: 3, spacing: 'sm' },
                        { minWidth: 'lg', cols: 3, spacing: 'sm' },
                        { minWidth: 'md', cols: 2, spacing: 'sm' },
                        { minWidth: 'sm', cols: 1, spacing: 'sm' },
                        { minWidth: 'xs', cols: 1, spacing: 'sm' },
                    ]}
                >
                    <Box>
                        <Card
                            shadow=''
                            radius="sm"
                            withBorder={true}
                            pos='relative'
                            mb='1.5rem'

                            style={{
                                borderColor: theme.colorScheme === 'light' ? theme.colors.gray[2] : theme.colors.dark[7],
                                backgroundColor: theme.colorScheme === 'light' ? '#fff' : theme.colors.dark[7],
                                overflow: 'unset',
                            }}
                        >
                            <Card.Section p='sm'>
                                <LoadingOverlay visible={data.dataInitLoading} overlayBlur={0} />

                                <InputComponent
                                    stateId='update-email-1'
                                    stateWithAsterisk={false}
                                    stateLabelFullWidth={true}
                                    stateLabel='Email'
                                    stateDescription='Email ini akan digunakan untuk menerima verifikasi reset username dan password akun Anda pada saat Anda lupa username atau password pada saat akan sign in aplikasi.'
                                    statePlaceHolder='Email'
                                    stateReadOnly={false}
                                    stateValue={safeValue(data.email.value, '')}
                                    stateError={data.email.valueError}
                                    fnOnChange={(e) => setData(d => ({ ...d, email: { ...d.email, value: regexNoSpace(e.target.value) } }))}
                                />

                                <Button
                                    mt='1rem'
                                    size='xs'
                                    loading={data.email.submitLoading}
                                    onClick={submitEmail}
                                >
                                    Ubah Email
                                </Button>
                            </Card.Section>
                        </Card>
                    </Box>
                </SimpleGrid>
            </Container>

            <ModalResponse
                stateShow={data.modalResponse.show}
                fnHide={() => setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        show: false
                    }
                }))}

                stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
                stateType={data.modalResponse.type}
                stateContent={data.modalResponse.content}
            />
        </React.Fragment>
    )
}

export default AccountResetEmail;